import React, { useState } from "react";
import Page from "../components/Page";
import {
  IconHistory,
  IconKey,
  IconArrowLeft,
  IconSearch,
  IconCalendar,
  IconCheck,
  IconAlertTriangle,
  IconX,
  IconLock,
  IconBed,
  IconUser,
  IconFilter
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useRfidCards, useRfidOperations } from "../controllers/rfid.controller";
import { Link } from "react-router-dom";

export default function RfidCardHistoryPage() {
  const [activeTab, setActiveTab] = useState("operations"); // 'operations' or 'cards'
  const [opsPage, setOpsPage] = useState(1);
  const [cardsPage, setCardsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { data: opsData, isLoading: loadingOps } = useRfidOperations({
    page: opsPage,
    per_page: 25
  });

  const { data: cardsData, isLoading: loadingCards } = useRfidCards({
    status: statusFilter,
    search: searchTerm,
    page: cardsPage,
    per_page: 25
  });

  const operations = opsData?.operations || [];
  const opsTotal = opsData?.total || 0;
  const opsTotalPages = opsData?.totalPages || 1;

  const cards = cardsData?.cards || [];
  const cardsTotal = cardsData?.total || 0;
  const cardsTotalPages = cardsData?.totalPages || 1;

  return (
    <Page className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/rfid-doorlock"
            className="p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
          >
            <IconArrowLeft size={20} />
          </Link>
          <div className="p-2.5 bg-purple-100 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 rounded-2xl">
            <IconHistory stroke={iconStroke} size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              RFID Card History & Operations Audit
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Audit trail of every keycard operation, cancellation reason, and operator activity
            </p>
          </div>
        </div>

        {/* Tab switch */}
        <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-neutral-800 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab("operations")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              activeTab === "operations"
                ? "bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-xs"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
            }`}
          >
            Operations Log ({opsTotal})
          </button>
          <button
            onClick={() => setActiveTab("cards")}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
              activeTab === "cards"
                ? "bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-xs"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
            }`}
          >
            All Card Records ({cardsTotal})
          </button>
        </div>
      </div>

      {/* OPERATIONS LOG VIEW */}
      {activeTab === "operations" && (
        <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
          {operations.length === 0 ? (
            <div className="p-12 text-center text-xs text-gray-500">
              No operations logged yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="py-3 px-4">Timestamp</th>
                    <th className="py-3 px-4">Action</th>
                    <th className="py-3 px-4">Card #</th>
                    <th className="py-3 px-4">Lock</th>
                    <th className="py-3 px-4">Method</th>
                    <th className="py-3 px-4">Operator</th>
                    <th className="py-3 px-4">Result</th>
                    <th className="py-3 px-4">Details / Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                  {operations.map((op) => (
                    <tr key={op.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition">
                      <td className="py-3 px-4 text-gray-500 font-mono text-[11px]">
                        {new Date(op.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 font-bold capitalize text-gray-900 dark:text-white">
                        {op.operation_type}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-gray-700 dark:text-gray-300">
                        {op.card_number || "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-800 dark:text-gray-200">
                        {op.lock_name || "—"}
                      </td>
                      <td className="py-3 px-4 capitalize text-gray-500">
                        {op.method || "—"}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {op.operator || "System"}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          op.status === 'success'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                            : op.status === 'failed'
                            ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                        }`}>
                          {op.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-500 text-[11px] max-w-xs truncate">
                        {op.error_message ? (
                          <span className="text-rose-600 font-semibold">{op.error_message}</span>
                        ) : (
                          op.idempotency_key ? `Key: ${op.idempotency_key}` : "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {opsTotalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-gray-200 dark:border-neutral-700 text-xs">
              <span className="text-gray-500">Page {opsPage} of {opsTotalPages} ({opsTotal} total operations)</span>
              <div className="flex gap-1">
                <button
                  disabled={opsPage <= 1}
                  onClick={() => setOpsPage(opsPage - 1)}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={opsPage >= opsTotalPages}
                  onClick={() => setOpsPage(opsPage + 1)}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ALL CARD RECORDS VIEW */}
      {activeTab === "cards" && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-neutral-800 p-1 rounded-xl">
              {[
                { id: "", label: "All Statuses" },
                { id: "active", label: "Active" },
                { id: "expired", label: "Expired" },
                { id: "cancelled", label: "Revoked" },
                { id: "lost", label: "Lost" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setStatusFilter(tab.id); setCardsPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition ${
                    statusFilter === tab.id
                      ? "bg-white dark:bg-neutral-700 text-gray-900 dark:text-white shadow-xs"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="relative w-full md:w-64">
              <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search card #, guest, room..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCardsPage(1); }}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
            {cards.length === 0 ? (
              <div className="p-12 text-center text-xs text-gray-500">
                No cards match the filter criteria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-50 dark:bg-neutral-900/50 border-b border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider text-[11px]">
                    <tr>
                      <th className="py-3 px-4">Card #</th>
                      <th className="py-3 px-4">Room</th>
                      <th className="py-3 px-4">Guest</th>
                      <th className="py-3 px-4">Method</th>
                      <th className="py-3 px-4">Issued At</th>
                      <th className="py-3 px-4">Valid Until</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Cancellation Info</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                    {cards.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-neutral-700/30 transition">
                        <td className="py-3 px-4 font-mono font-bold text-gray-900 dark:text-white">
                          {c.card_number}
                        </td>
                        <td className="py-3 px-4 font-bold text-gray-800 dark:text-gray-200">
                          Room {c.room_name || "—"}
                        </td>
                        <td className="py-3 px-4 text-gray-700 dark:text-gray-300">
                          {c.guest_name || "Walk-in Guest"}
                        </td>
                        <td className="py-3 px-4 capitalize text-gray-500">
                          {c.issuing_method}
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-[11px]">
                          {c.issued_at ? new Date(c.issued_at).toLocaleString() : "—"}
                          {c.issued_by ? ` by ${c.issued_by}` : ""}
                        </td>
                        <td className="py-3 px-4 text-gray-600 dark:text-gray-300 font-medium">
                          {c.valid_until ? new Date(c.valid_until).toLocaleString() : "Permanent"}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            c.status === 'active'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
                              : c.status === 'lost'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300'
                          }`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-[11px]">
                          {c.cancelled_at ? (
                            <div>
                              <div>{c.cancel_reason || "Revoked"} ({new Date(c.cancelled_at).toLocaleDateString()})</div>
                              <div className="text-[10px] text-gray-400">by {c.cancelled_by || "reception"}</div>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {cardsTotalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t border-gray-200 dark:border-neutral-700 text-xs">
                <span className="text-gray-500">Page {cardsPage} of {cardsTotalPages} ({cardsTotal} total records)</span>
                <div className="flex gap-1">
                  <button
                    disabled={cardsPage <= 1}
                    onClick={() => setCardsPage(cardsPage - 1)}
                    className="px-3 py-1 rounded border disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    disabled={cardsPage >= cardsTotalPages}
                    onClick={() => setCardsPage(cardsPage + 1)}
                    className="px-3 py-1 rounded border disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
}
